import React, { useState, useEffect } from 'react';
import { Database, Check, ArrowRight, Folder, FolderOpen, Calendar, Plus, X, FileText, ChevronRight, ChevronDown } from 'lucide-react';
import '../styles/DataSetPreparation.css';

const DataSetPreparation = ({ 
  dataSets, 
  selectedDataSet, 
  setSelectedDataSet, 
  newDataSetName, 
  setNewDataSetName, 
  handleCreateDataSet, 
  handleOpenDataSet, 
  setCurrentStep 
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [showDirectoryTree, setShowDirectoryTree] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState(['sams', 'tess', 'data']);

  // Sample data sets based on the image
  const [sampleDataSets] = useState([
    { name: 'JA9697CH', status: 'Active', created: '2024-01-15', lastModified: '2024-01-20' },
    { name: 'SECDATA', status: 'Draft', created: '2024-01-10', lastModified: '2024-01-18' },
    { name: 'TABLE1', status: 'Active', created: '2024-01-05', lastModified: '2024-01-15' },
    { name: 'TEST', status: 'Draft', created: '2024-01-01', lastModified: '2024-01-12' },
    { name: 'TEST1', status: 'Draft', created: '2024-01-02', lastModified: '2024-01-14' },
    { name: 'TT', status: 'Active', created: '2024-01-08', lastModified: '2024-01-19' }
  ]);

  // Directory tree structure based on the image
  const directoryTree = {
    name: 'sams',
    type: 'folder',
    children: [
      {
        name: 'tess',
        type: 'folder',
        children: [
          {
            name: 'data',
            type: 'folder',
            children: [
              { name: '96fung', type: 'folder', isDataSet: true },
              { name: '96hwc', type: 'folder', isDataSet: true },
              { name: '96hwc1', type: 'folder', isDataSet: true },
              { name: '96hwc2', type: 'folder', isDataSet: true },
              { name: 'uat2101', type: 'folder', isDataSet: true }
            ]
          }
        ]
      },
      { name: 'appl', type: 'folder' },
      { name: 'exec', type: 'folder' },
      { name: 'c', type: 'folder' },
      { name: 'planner', type: 'folder' }
    ]
  };

  const handleInputChange = (value) => {
    // Limit to 8 characters and only allow alphanumeric
    const filteredValue = value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8);
    setNewDataSetName(filteredValue);
  };

  const handleCreateNewDataSet = async () => {
    if (newDataSetName.trim()) {
      setIsCreating(true);
      try {
        await handleCreateDataSet();
        setIsCreating(false);
      } catch (error) {
        console.error('Error creating data set:', error);
        setIsCreating(false);
      }
    }
  };

  const handleOpenExistingDataSet = () => {
    if (selectedDataSet) {
      handleOpenDataSet(selectedDataSet);
    }
  };

  const toggleFolder = (folderName) => {
    setExpandedFolders(prev => 
      prev.includes(folderName) 
        ? prev.filter(f => f !== folderName)
        : [...prev, folderName]
    );
  };

  const renderDirectoryNode = (node, level = 0) => {
    const isExpanded = expandedFolders.includes(node.name);
    const hasChildren = node.children && node.children.length > 0;
    const isDataSet = node.isDataSet;

    return (
      <div key={node.name} className="directory-node" style={{ marginLeft: `${level * 16}px` }}>
        <div 
          className={`directory-item ${isDataSet ? 'data-set-item' : ''} ${selectedDataSet?.name === node.name ? 'selected' : ''}`}
          onClick={() => {
            if (hasChildren) {
              toggleFolder(node.name);
            } else if (isDataSet) {
              setSelectedDataSet({ name: node.name, status: 'Active' });
            }
          }}
        >
          <div className="directory-icon">
            {hasChildren ? (
              isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
            ) : (
              <Folder size={14} />
            )}
          </div>
          <span className="directory-name">{node.name}</span>
          {isDataSet && <span className="data-set-badge">Data Set</span>}
        </div>
        
        {hasChildren && isExpanded && (
          <div className="directory-children">
            {node.children.map(child => renderDirectoryNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dataset-container">
      {/* Header */}
      <div className="dataset-header">
        <div className="dataset-header-content">
          <div className="dataset-icon">
            <Database className="icon" />
          </div>
          <div className="dataset-title-section">
            <h1 className="dataset-title">Data Set Preparation Module</h1>
            <p className="dataset-subtitle">建立資料集 (Data Set) - 管理和建立時間表資料集</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dataset-content">
        {/* Open Timetable Section */}
        <div className="open-timetable-section">
          <div className="section-header">
            <h3 className="section-title">Open Timetable:</h3>
          </div>
          
          <div className="input-section">
            <input
              type="text"
              value={newDataSetName}
              onChange={(e) => handleInputChange(e.target.value)}
              className="dataset-input"
              placeholder="Enter new data set name (max 8 characters)"
              maxLength={8}
            />
            <div className="input-info">
              <span className="char-count">{newDataSetName.length}/8</span>
              <span className="char-limit">Alphanumeric only</span>
            </div>
          </div>

          <div className="data-sets-list">
            <div className="list-header">
              <span className="list-title">Existing Data Sets</span>
              <span className="list-count">{sampleDataSets.length} items</span>
            </div>
            <div className="list-container">
              {sampleDataSets.map((dataSet, index) => (
                <div
                  key={index}
                  className={`list-item ${selectedDataSet?.name === dataSet.name ? 'selected' : ''}`}
                  onClick={() => setSelectedDataSet(dataSet)}
                >
                  <div className="item-content">
                    <div className="item-name">{dataSet.name}</div>
                    <div className="item-status">
                      <span className={`status-badge ${dataSet.status.toLowerCase()}`}>
                        {dataSet.status}
                      </span>
                    </div>
                  </div>
                  <div className="item-details">
                    <span className="item-date">Created: {dataSet.created}</span>
                    <span className="item-date">Modified: {dataSet.lastModified}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Directory Tree Section */}
        <div className="directory-section">
          <div className="section-header">
            <h3 className="section-title">資料集 (Data Set) 目錄結構</h3>
            <button
              className="toggle-button"
              onClick={() => setShowDirectoryTree(!showDirectoryTree)}
            >
              {showDirectoryTree ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          </div>
          
          {showDirectoryTree && (
            <div className="directory-tree">
              <div className="tree-container">
                {renderDirectoryNode(directoryTree)}
              </div>
              <div className="tree-info">
                <div className="info-icon">💡</div>
                <div className="info-text">
                  新的資料集會以子目錄形式新增於 DATA 目錄之下
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Selected Data Set Info */}
        {selectedDataSet && (
          <div className="selected-info">
            <div className="info-header">
              <Check className="icon" />
              <h4 className="info-title">已選資料集</h4>
            </div>
            <div className="info-details">
              <div className="detail-row">
                <span className="detail-label">名稱:</span>
                <span className="detail-value">{selectedDataSet.name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">建立:</span>
                <span className="detail-value">{selectedDataSet.created}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">修改:</span>
                <span className="detail-value">{selectedDataSet.lastModified}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">狀態:</span>
                <span className={`detail-value status-badge ${selectedDataSet.status.toLowerCase()}`}>
                  {selectedDataSet.status}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="dataset-actions">
        <button
          onClick={() => console.log('Exit')}
          className="action-btn exit-btn"
        >
          <X size={16} />
          Exit
        </button>
        
        <div className="action-group">
          <button
            onClick={handleOpenExistingDataSet}
            disabled={!selectedDataSet}
            className="action-btn ok-btn"
          >
            <Check size={16} />
            OK
          </button>
          
          <button
            onClick={handleCreateNewDataSet}
            disabled={!newDataSetName.trim() || isCreating}
            className="action-btn create-btn"
          >
            {isCreating ? (
              <div className="loading-spinner" />
            ) : (
              <Plus size={16} />
            )}
            {isCreating ? 'Creating...' : 'Create New'}
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="instructions-section">
        <h4 className="instructions-title">操作說明</h4>
        <div className="instructions-content">
          <p><strong>1. 開啟現有資料集：</strong> 從列表中選擇現有的資料集，然後點擊 "OK" 按鈕。</p>
          <p><strong>2. 建立新資料集：</strong> 在輸入框中輸入新資料集名稱（最多8個字元），然後點擊 "Create New" 按鈕。</p>
          <p><strong>3. 資料集限制：</strong> 資料集名稱只能包含英文字母和數字，且不能超過8個字元。</p>
          <p><strong>4. 目錄結構：</strong> 所有資料集都儲存在 \SAMS\TESS\DATA 目錄下作為子目錄。</p>
        </div>
      </div>
    </div>
  );
};

export default DataSetPreparation;